import cx from "classnames";
import React, { memo, useCallback, useMemo, useState } from "react";
import { Col, Form, Row } from "reactstrap";
import { JobWarning } from "../../../../../common/api/v1.1/types/jobs";

import { StateCallbacks } from "../../state";
import { buildValueKey, getConfidenceState } from "../../state/util";
import { Block, DataPointValue, GroupTaxonomyEntry, NonGroupTaxonomyEntry, State } from "../../types";
import { Answer } from "../Answer";
import { GroupValues, MultipleGroupActions } from "./MultipleGroupActions";
import { QuestionTitle } from "./QuestionTitle";
import { useExtractGroupValues } from "./useExtractGroupValues";

type Props = Readonly<{
  active: State["activeValue"];
  confidenceThreshold: State["confidenceThreshold"];
  group: GroupTaxonomyEntry;
  review: boolean;
  stateCallbacks: StateCallbacks;
  taxonomy: State["taxonomy"];
  values: State["values"];
  blocks: {
    [blockId: string]: Block;
  };
  focusWarningsTab: () => void;
  warnings: JobWarning[];
}>;

const newKey = (
  group: GroupTaxonomyEntry,
  lastDataPoint: DataPointValue<any>,
  dataPointKey: string,
): string => {
  const keySplit = lastDataPoint.valueKey.split(".");
  const lastIndex = parseInt(keySplit[group.multiple ? 3 : 2], 10);
  let newGroupIndex = null;
  if (group.multiple) {
    newGroupIndex = parseInt(keySplit[1], 10);
  }
  return buildValueKey(group.id, newGroupIndex, dataPointKey, lastIndex + 1);
};

const GroupQuestionComponent = ({
  active,
  confidenceThreshold,
  group,
  review,
  stateCallbacks,
  taxonomy,
  values,
  blocks,
  focusWarningsTab,
  warnings
}: Props) => {

  const showMultiple = useMemo(() => group.children.some(child => child.multiple), [
    group.children,
    taxonomy,
  ]);
  const onSubmit = useCallback(e => {
    e.preventDefault();
  }, []);

  const groupValueKeys = useExtractGroupValues(group.id, group.multiple, taxonomy, values);
  const groupValues = useMemo((): GroupValues => {
    return groupValueKeys.map(g =>
      Object.keys(g).reduce((prev: any, next) => {
        prev[next] = g[next].map(v => {
          if (v in values) {
            return values[v];
          }
          throw new Error(`Key: ${v} not found in values.`);
        });
        return prev;
      }, {}),
    );
  }, [groupValueKeys, values]);
  
  const [collapsedGroups, setCollapsedGroups] = useState<{ [key: string]: true }>({});
  const onRemove = useCallback(
    (key: string, disableRemove: boolean) => {
      if (disableRemove) {
        stateCallbacks.onClearValue(key);
        return;
      }
      stateCallbacks.onRemoveAnswer(key);
    },
    [stateCallbacks.onClearValue, stateCallbacks.onRemoveAnswer],
  );
  return (
    <Form onSubmit={onSubmit}>
      <QuestionTitle label={!!group.description ? group.description : group.id} />
      <Row>
        <Col xs={12}>
          {groupValues.map((g, i) => {
            const split = g[Object.keys(g)[0]][0].valueKey.split(".");
            let groupKey = split[0];
            if (group.multiple) {
              groupKey = `${split[0]}.${split[1]}`;
            }
            return (
              <div
                className={cx("grouped-container")}
                key={groupKey}
              >
                {group.multiple && (
                  <MultipleGroupActions
                    collapsed={groupKey in collapsedGroups}
                    group={group}
                    index={i}
                    onAddGroup={() => {
                      const taxonomyKeys: Array<string> = [];
                      const lastGroup = groupValues[groupValues.length - 1];
                      const lastGroupIndex = parseInt(
                        lastGroup[Object.keys(lastGroup)[0]][0].valueKey.split(".")[1],
                        10,
                      );
                      const valueKeys: Array<string> = Object.keys(g).reduce((prev, next) => {
                        for (const value of g[next]) {
                          taxonomyKeys.push(value.taxonomyKey);
                          prev.push(
                            buildValueKey(
                              group.id,
                              lastGroupIndex + 1,
                              next,
                              group.children?.find(x => x.id === next)?.multiple ? 1 : null,
                            ),
                          );
                        }
                        return prev;
                      }, [] as Array<string>);
                      stateCallbacks.onAddGroup(taxonomyKeys, valueKeys);
                    }}
                    onClearGroup={() => {
                      stateCallbacks.onClearGroup(groupKey);
                    }}
                    onDuplicateGroup={() => {
                      const taxonomyKeys: Array<string> = [];
                      const valueKeys: Array<string> = [];
                      const lastGroup = groupValues[groupValues.length - 1];
                      const lastGroupIndex =
                        parseInt(
                          lastGroup[Object.keys(lastGroup)[0]][0].valueKey.split(".")[1],
                          10,
                        ) + 1;
                      const counts: { [key: string]: number } = {};
                      const copyValues: Array<DataPointValue<any>> = Object.keys(g).reduce(
                        (prev, next) => {
                          for (const value of g[next]) {
                            prev.push(value);
                            taxonomyKeys.push(value.taxonomyKey);
                            let valueIndex = null;
                            if (group.children?.find(x => x.id === next)?.multiple) {
                              if (next in counts) {
                                counts[next]++;
                                valueIndex = counts[next];
                              } else {
                                counts[next] = 1;
                                valueIndex = 1;
                              }
                            }
                            valueKeys.push(
                              buildValueKey(group.id, lastGroupIndex, next, valueIndex),
                            );
                          }
                          return prev;
                        },
                        [] as Array<DataPointValue<any>>,
                      );
                      stateCallbacks.onDuplicateGroup(copyValues, taxonomyKeys, valueKeys);
                    }}
                    onRemoveGroup={() => {
                      stateCallbacks.onRemoveGroup(
                        Object.keys(g).reduce((prev: Array<string>, next: string): Array<
                          string
                        > => {
                          return prev.concat(g[next].map(v => v.valueKey));
                        }, [] as Array<string>),
                      );
                    }}
                    onToggleCollapse={() => {
                      if (groupKey in collapsedGroups) {
                        const newCollapsedState = { ...collapsedGroups };
                        delete newCollapsedState[groupKey];
                        setCollapsedGroups(newCollapsedState);
                        return;
                      }
                      setCollapsedGroups({
                        ...collapsedGroups,
                        [groupKey]: true,
                      });
                    }}
                    review={review}
                    taxonomy={taxonomy}
                    values={groupValues}
                  />
                )}
                {!(groupKey in collapsedGroups) &&
                  group.children.map(child => {
                    let dataPointKey = child.id.toLocaleLowerCase();
                    if (!(dataPointKey in g)) {
                      return null;
                    }
                    const dataPoint = child;

                    const disableAdd = !dataPoint.multiple;
                    const disableRemove = !dataPoint.multiple || g[dataPointKey].length < 2;
                    return g[dataPointKey].map(value => 
                      (
                      // It appears that all answers will get re-rendered whenever 
                      // the value of a single Answer changes.  Reason is still unknown,
                      // but it should be looked into.  For very large taxonomies it will cause things to 
                      // become sluggish. -SB
                      <Answer
                        active={value.valueKey === active}
                        confidenceState={getConfidenceState(confidenceThreshold)(value)}
                        dataPoint={dataPoint}
                        disableAdd={disableAdd}
                        disableRemove={disableRemove}
                        id={value.valueKey}
                        key={value.valueKey}
                        label={dataPoint.question}
                        onAddValue={() => {
                          if (!dataPoint.multiple) {
                            return;
                          }
                          stateCallbacks.onAddValue(
                            dataPointKey,
                            newKey(
                              group,
                              g[dataPointKey][g[dataPointKey].length - 1],
                              dataPointKey,
                            ),
                          );
                        }}
                        onDuplicateValue={() => {
                          if (!dataPoint.multiple) {
                            return;
                          }

                          stateCallbacks.onDuplicateAnswer(
                            dataPointKey,
                            newKey(
                              group,
                              g[dataPointKey][g[dataPointKey].length - 1],
                              dataPointKey,
                            ),
                            value,
                          );
                        }}
                        onFocus={stateCallbacks.onSelectDataPoint}
                        onRemove={onRemove}
                        onScrollToBlock={stateCallbacks.onSetScrollToBlock}
                        onUpdate={stateCallbacks.onUpdateValue}
                        review={review}
                        showLabel
                        showMultiple={showMultiple}
                        value={value}
                        block={blocks[value?.idRef as string]}
                        focusWarningsTab={focusWarningsTab}
                        hasWarning={!! warnings && !!warnings.find(x => x.dataPoint === value.valueKey)}
                      />
                    ));
                  })}
              </div>
            );
          })}
        </Col>
      </Row>
    </Form>
  );
};
GroupQuestionComponent.displayName = "ActiveGroupQuestion";
export const GroupQuestion = memo(GroupQuestionComponent);
